<script lang="ts">
  import type { Topic } from '../lib/types'
  import { Dialog, Input, Textarea, Label, Button, Checkbox } from '$lib/components/ui'

  interface Props {
    topic: Topic
    videoId: string
    onSave: (topic: Topic) => void
    onCancel: () => void
  }

  let { topic, videoId, onSave, onCancel }: Props = $props()

  let name = $state(topic.name)
  let description = $state(topic.description || '')
  let aliases = $state(topic.aliases?.join(', ') || '')
  let isAdvertisement = $state(topic.isAdvertisement || false)
  let internalOnly = $state(topic.internalOnly || false)
  let isSaving = $state(false)
  let open = $state(true)

  async function handleSave() {
    if (!name.trim()) return

    isSaving = true
    try {
      const aliasesList = aliases
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0)

      const payload = {
        name: name.trim(),
        description: description.trim(),
        aliases: aliasesList,
        is_advertisement: isAdvertisement,
        internal_only: internalOnly,
        video_id: videoId,
      }

      const response = await fetch(`/api/topics/${topic.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const savedTopic = await response.json()
        onSave(savedTopic)
      }
    } finally {
      isSaving = false
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) onCancel()
  }
</script>

<Dialog bind:open onOpenChange={handleOpenChange} title="Edit topic">
  <div class="space-y-4">
    <div>
      <Label for="name">Name</Label>
      <Input id="name" bind:value={name} placeholder="Topic name" />
    </div>

    <div>
      <Label for="aliases">Aliases (comma-separated)</Label>
      <Input id="aliases" bind:value={aliases} placeholder="Alternative names" />
    </div>

    <div>
      <Label for="description">Description</Label>
      <Textarea id="description" bind:value={description} placeholder="Description" rows={3} />
    </div>

    <div class="flex items-center gap-2">
      <Checkbox bind:checked={isAdvertisement} />
      <span class="text-sm text-text-secondary">Is advertisement</span>
    </div>

    <div class="flex items-center gap-2">
      <Checkbox bind:checked={internalOnly} />
      <span class="text-sm text-text-secondary">Internal only (don't match cross-video)</span>
    </div>

    <div class="flex justify-end gap-2 pt-4 border-t border-border-subtle">
      <Button variant="ghost" onclick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onclick={handleSave} disabled={isSaving || !name.trim()}>
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
</Dialog>
